/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { Alerts } from "@components/common/AlertModal";
import axios, { AxiosError } from "axios";
import { useSelector } from "react-redux";
import { RootState } from "../store/reducer";
import { colors, Title1 } from "@utils/ui_palette";
import { Link } from "react-router-dom";
import PageNavigation from "@components/common/PageNavigation";

interface IProfile {
  id: number;
  nick: string;
  thumb_imageprofile: string;
}

interface IReport {
  createdAt: string;
  grade_type: number;
  id: number;
  memo: null;
  paper_id: number;
  perfect_score: number;
  profile_id: number;
  save_at: string;
  save_gb: number;
  total_score: number;
  updatedAt: string;
  profiles: IProfile;
}

function UserDetail() {
  const accessToken = useSelector(
    (state: RootState) => state.user.userInfo.accessToken
  );
  const [report, setReport] = useState<null | IReport[]>(null);
  const [pagination, setPagination] = useState<number>(1);
  const [totalpage, setTotalpage] = useState<number>(0);

  const getProfileReport = useCallback(
    async (page: number) => {
      setPagination(page);

      try {
        const { data } = await axios.post(
          `${process.env.REACT_APP_API}/admin/list?page=${page}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        setReport(data.quater_list);
        setTotalpage(data.total);
      } catch (error) {
        const errorResponse = (error as AxiosError).response;

        if (errorResponse && (errorResponse.data as any).message) {
          Alerts.show({
            title: (errorResponse.data as any).message,
          });
        }
      }
    },
    [accessToken, setReport, pagination, setPagination]
  );

  useEffect(() => {
    getProfileReport(1);
  }, []);

  return (
    <Container>
      <TitleBox>
        <Title1 align="left">Assessment List</Title1>
      </TitleBox>

      {report && report.length > 0 && (
        <Panel>
          <ReportCardGroup>
            {report.map((data) => (
              <Link
                to={`/assessment/${data.id}?grade_type=${data.grade_type}`}
                key={`report-${data.id}`}
                target="_blank"
              >
                <RportCardItem>
                  <ProfileBox>
                    <Profiles src={data.profiles.thumb_imageprofile} />
                  </ProfileBox>
                  <ReportInfo>
                    <ReportTitle>
                      <ReportName>
                        Level {data.grade_type.toString()[1]}
                        <span>{data.profiles.nick}</span>
                      </ReportName>

                      {data.save_gb === 2 ? (
                        <ReportState type={data.save_gb}>
                          Proceeding
                        </ReportState>
                      ) : (
                        <ReportState type={data.save_gb}>Done</ReportState>
                      )}
                    </ReportTitle>
                    <ReportDescription>
                      <ReportUserBox>
                        <ReportBox>
                          <ReportDescTitle>Age</ReportDescTitle>
                          <ReportDescInfo>
                            {data.grade_type.toString()[0]} years old
                          </ReportDescInfo>
                        </ReportBox>
                        <ReportBox>
                          <ReportDescTitle>Exam date</ReportDescTitle>
                          <ReportDescInfo>
                            {data.save_at.split("T")[0]}
                          </ReportDescInfo>
                        </ReportBox>
                      </ReportUserBox>
                      {data.save_gb !== 2 && (
                        <ScoreBox>
                          <Score>{data.total_score}</Score>
                          <PerfectScore>/ {data.perfect_score}</PerfectScore>
                        </ScoreBox>
                      )}
                    </ReportDescription>
                  </ReportInfo>
                </RportCardItem>
              </Link>
            ))}
          </ReportCardGroup>
        </Panel>
      )}

      {report && report.length === 0 && (
        <Panel>There is no assessment data.</Panel>
      )}
      {report && totalpage > 10 && (
        <PagenationContain>
          <PageNavigation
            activePage={pagination}
            itemsCountPerPage={10}
            totalItemsCount={totalpage}
            pageRangeDisplayed={5}
            onChange={(page) => getProfileReport(page)}
          />
        </PagenationContain>
      )}
    </Container>
  );
}

const Container = styled.div`
  width: 1024px;
  padding: 20px;
  padding-bottom: 200px;
`;

const ProfileBox = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  overflow: hidden;
`;

const Profiles = styled("img")`
  width: 100%;
`;

const ReportName = styled.div`
  font-size: 18px;
  color: ${colors.Gray50};

  span {
    font-size: 24px;
    font-weight: 700;
    color: ${colors.Primary50};
    margin-left: 10px;
  }
`;

const TitleBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Panel = styled.div`
  display: flex;
  flex-wrap: wrap;
  padding: 20px;
  border-radius: 12px;
  background-color: ${colors.White};
  border: 1px solid ${colors.Gray30};
  margin-top: 20px;
  box-shadow: 5px 5px 10px ${colors.Gray30};
`;

const ReportCardGroup = styled.div`
  flex-basis: 100%;
`;

const RportCardItem = styled.div`
  border: 1px solid ${colors.Gray30};
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 20px;
  box-shadow: 5px 5px 10px ${colors.Gray30};
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.4s;
  background-color: ${colors.White};

  &:hover {
    transition: all 0.4s;
    transform: scale(1.02);
  }
`;

const ReportInfo = styled.div`
  padding-left: 20px;
  flex: 1;
`;

const ReportTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 20px;
`;

const ReportState = styled.div<{ type: number }>`
  color: ${colors.White};
  font-size: 18px;
  background-color: ${(prpos) =>
    prpos.type === 2 ? colors.Secondary50 : colors.Primary50};
  width: 150px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50px;
`;

const ReportDescription = styled.div`
  padding-top: 20px;
  border-top: 1px solid ${colors.Gray30};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ReportBox = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const ReportDescTitle = styled.div`
  flex-basis: 100px;
  font-size: 15px;
  color: ${colors.Gray50};
`;

const ReportDescInfo = styled.div`
  font-size: 15px;
  color: ${colors.Black};
`;

const ReportUserBox = styled.div`
  flex: 1;
`;

const ScoreBox = styled.div`
  display: flex;
  align-items: flex-end;
`;

const Score = styled.div`
  margin-right: 4px;
  font-size: 30px;
  font-weight: 700;
  color: ${colors.Primary50};
`;

const PerfectScore = styled.div`
  font-size: 30px;
  color: ${colors.Gray50};
`;

const PagenationContain = styled.div`
  margin-top: 80px;
`;

export default UserDetail;
